import InfoIcon from '@material-ui/icons/InfoOutlined'
import { logAmplitudeEventAllowCustomers } from 'amplitude/amplitude'
import { authSelectors } from 'ducks/auth'
import {
  BaseThemeType,
  Checkbox,
  FormControlLabel,
  LoadingDots,
  Slider3,
  styled,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from 'opensolar-ui'
import { useLocale } from 'ra-core'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  CheckoutFinancingFilterParameters,
  CheckoutFinancingPricingParams,
  FinanceMarketplaceSystemDetails,
} from '../types'
import { formatCurrencyWithSymbol } from '../utils'

type PropTypes = {
  pricingParams: CheckoutFinancingPricingParams
  updatePricingParams: (newParams: CheckoutFinancingPricingParams) => void
  filterParameters: CheckoutFinancingFilterParameters
  isLoading: boolean
  systemDetails: FinanceMarketplaceSystemDetails | null
}

const MainWrapper = styled('div')(({ theme }: { theme: BaseThemeType }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '24px',
  backgroundColor: '#FAFBFC',
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '10px',
  flex: 1,
  position: 'relative',
}))

const SliderContainer = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    flex: isMobile ? 4 : 6,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
  })
)

const SliderValueDisplay = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.grey[800],
}))

const InputContainer = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    flex: isMobile ? 4 : 2,
  })
)

const ReadOnlyTextField = styled(TextField)(({ theme }: { theme: BaseThemeType }) => ({
  '& .OSUI-InputBase-root': {
    minHeight: 'unset',
  },
  '& .OSUI-InputBase-input': {
    padding: 5,
  },
}))

const LoadingDotsWrapper = styled('div')(({ theme }: { theme: BaseThemeType }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  opacity: 0.75,
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.palette.background.paper,
}))

const BorrowRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
})
const BorrowText = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  fontWeight: 600,
  lineHeight: '28px',
}))
const BorrowAmountText = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  fontWeight: 600,
  lineHeight: '28px',
  color: theme.palette.blue[800],
  marginLeft: '10px',
}))

const StyledSlider3 = styled(Slider3)(({ isXs }: { isXs: boolean }) => ({
  minWidth: isXs ? '80px !important' : '100px !important',
}))

const FieldWrapper = styled('div')(({ theme }: { theme: BaseThemeType }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
}))

const SliderRow = styled('div')(({ theme }: { theme: BaseThemeType }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  width: '100%',
}))

const CheckboxContainer = styled('div')(({ theme }: { theme: BaseThemeType }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  width: '100%',
}))

const SliderValuesRow = styled('div')(({ theme }: { theme: BaseThemeType }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  marginRight: '8px',
}))

const DepositAmountRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 8,
})

const StyledInfoIcon = styled(InfoIcon)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.blue[800],
}))

const SectionTitle = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.blue[800],
}))

const FinanceMarketplaceFinancingCard = (props: PropTypes) => {
  const upfrontPayment = props.pricingParams.upfront_payment || 0
  const [financingTermStart, setFinancingTermStart] = useState(
    Math.max(props.pricingParams.term_start || 0, props.filterParameters?.minimum_term || 0)
  )
  const [financingTermStartTemp, setFinancingTermStartTemp] = useState(
    Math.max(props.pricingParams.term_start || 0, props.filterParameters?.minimum_term || 0)
  )
  const [financingTermEnd, setFinancingTermEnd] = useState(
    Math.min(props.pricingParams.term_end || 100, props.filterParameters?.maximum_term || 100)
  )
  const [financingTermEndTemp, setFinancingTermEndTemp] = useState(
    Math.min(props.pricingParams.term_end || 100, props.filterParameters?.maximum_term || 100)
  )
  const [monthlyRepaymentStart, setMonthlyRepaymentStart] = useState(
    Math.max(props.pricingParams.monthly_repayment_start || 0, props.filterParameters?.minimum_monthly_repayment || 0)
  )
  const [monthlyRepaymentStartTemp, setMonthlyRepaymentStartTemp] = useState(
    Math.max(props.pricingParams.monthly_repayment_start || 0, props.filterParameters?.minimum_monthly_repayment || 0)
  )
  const [monthlyRepaymentEnd, setMonthlyRepaymentEnd] = useState(
    Math.min(
      props.pricingParams.monthly_repayment_end || 100000,
      props.filterParameters?.maximum_monthly_repayment || 100000
    )
  )
  const [monthlyRepaymentEndTemp, setMonthlyRepaymentEndTemp] = useState(
    Math.min(
      props.pricingParams.monthly_repayment_end || 100000,
      props.filterParameters?.maximum_monthly_repayment || 100000
    )
  )
  const [awaitingCalcsStart, setAwaitingCalcsStart] = useState(false)
  const [awaitingCalcsEnd, setAwaitingCalcsEnd] = useState(false)

  // after the user edited the deposit text input we wait for calcs to start, once they do we set awaitingCalcsEnd to true
  useEffect(() => {
    if (awaitingCalcsStart && props.isLoading && !awaitingCalcsEnd) {
      setAwaitingCalcsEnd(true)
      setAwaitingCalcsStart(false)
    }
  }, [awaitingCalcsStart, awaitingCalcsEnd, props.isLoading])

  const isPro = useSelector(authSelectors.getIsAuthenticatedPro)
  const locale = useLocale() || 'en'
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))
  const isXs = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('xs'))

  const termRange = useMemo(() => {
    return [
      Math.max(financingTermStartTemp, props.filterParameters?.minimum_term || 0),
      Math.min(financingTermEndTemp, props.filterParameters?.maximum_term || 100),
    ]
  }, [
    financingTermStartTemp,
    financingTermEndTemp,
    props.filterParameters?.minimum_term,
    props.filterParameters?.maximum_term,
  ])

  const minimumMonthlyRepayment = useMemo(() => {
    const value = props.filterParameters?.minimum_monthly_repayment || 0
    return Math.floor(value)
  }, [props.filterParameters?.minimum_monthly_repayment])

  const maximumMonthlyRepayment = useMemo(() => {
    const value = props.filterParameters?.maximum_monthly_repayment || 100000
    return Math.ceil(value)
  }, [props.filterParameters?.maximum_monthly_repayment])

  const repaymentRange = useMemo(() => {
    return [
      Math.max(monthlyRepaymentStartTemp, minimumMonthlyRepayment),
      Math.min(monthlyRepaymentEndTemp, maximumMonthlyRepayment),
    ]
  }, [monthlyRepaymentStartTemp, monthlyRepaymentEndTemp, minimumMonthlyRepayment, maximumMonthlyRepayment])

  const onTermChange = (event: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    setFinancingTermStartTemp(value[0])
    setFinancingTermEndTemp(value[1])
  }

  const onTermChangeCommitted = (event: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    setFinancingTermStart(value[0])
    setFinancingTermEnd(value[1])
    logAmplitudeEventAllowCustomers('marketplace_filter_changed', {
      org_id: props.pricingParams.org_id,
      project_id: props.pricingParams.project_id,
      filter: 'term',
      method: 'slider',
      value: `${value[0]} - ${value[1]}`,
    })
  }

  const readOnlyTermValue = useMemo(() => {
    return `${termRange[0]} to ${termRange[1]}`
  }, [termRange])

  const onRepaymentChange = (event: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    setMonthlyRepaymentStartTemp(value[0])
    setMonthlyRepaymentEndTemp(value[1])
  }

  const onRepaymentChangeCommitted = (
    event: Event | React.SyntheticEvent<Element, Event>,
    value: number | number[]
  ) => {
    setMonthlyRepaymentStart(value[0])
    setMonthlyRepaymentEnd(value[1])
    logAmplitudeEventAllowCustomers('marketplace_filter_changed', {
      org_id: props.pricingParams.org_id,
      project_id: props.pricingParams.project_id,
      filter: 'monthly_repayment',
      method: 'slider',
      value: `${value[0]} - ${value[1]}`,
    })
  }

  const readOnlyRepaymentValue = useMemo(() => {
    return `${formatCurrencyWithSymbol(
      repaymentRange[0],
      props.systemDetails?.currency_symbol,
      locale,
      0
    )} to ${formatCurrencyWithSymbol(repaymentRange[1], props.systemDetails?.currency_symbol, locale, 0)}`
  }, [repaymentRange, props.systemDetails?.currency_symbol, locale])

  const financingBasePrice = props.systemDetails?.system_price_payable ?? props.systemDetails?.system_price_including_tax

  const financedAmount = useMemo(() => {
    if (!financingBasePrice) return null
    const borrowedAmount = financingBasePrice - upfrontPayment
    return formatCurrencyWithSymbol(borrowedAmount, props.systemDetails?.currency_symbol, locale, 0)
  }, [upfrontPayment, locale, props.systemDetails?.currency_symbol, financingBasePrice])

  useEffect(() => {
    props.updatePricingParams({
      ...props.pricingParams,
      upfront_payment: upfrontPayment,
      term_start: financingTermStart,
      term_end: financingTermEnd,
      monthly_repayment_start: monthlyRepaymentStart,
      monthly_repayment_end: monthlyRepaymentEnd,
    })
  }, [upfrontPayment, financingTermStart, financingTermEnd, monthlyRepaymentStart, monthlyRepaymentEnd])

  return (
    <MainWrapper>
      {props.isLoading && (
        <LoadingDotsWrapper>
          <LoadingDots />
        </LoadingDotsWrapper>
      )}
      <BorrowRow>
        <BorrowText variant="h5">I would like to borrow:</BorrowText>
        <BorrowAmountText textVariant="h5">{financedAmount}</BorrowAmountText>
      </BorrowRow>
      <FieldWrapper>
        <SectionTitle textVariant="subtitle1">Deposit</SectionTitle>
        <DepositAmountRow>
          <Typography variant="h5">
            {formatCurrencyWithSymbol(upfrontPayment, props.systemDetails?.currency_symbol || ' ', locale, 0)}
          </Typography>
          <Tooltip title="If you want to change the deposit amount, please contact your installer.">
            <StyledInfoIcon />
          </Tooltip>
        </DepositAmountRow>
      </FieldWrapper>
      <FieldWrapper>
        <SectionTitle textVariant="subtitle1">Loan Term</SectionTitle>
        <SliderRow>
          <SliderContainer isMobile={isMobile}>
            <StyledSlider3
              isXs={isXs}
              min={props.filterParameters?.minimum_term || 0}
              max={props.filterParameters?.maximum_term || 30}
              value={termRange}
              onChangeCommitted={onTermChangeCommitted}
              onChange={onTermChange}
              step={1}
              disabled={props.isLoading}
            />
            <SliderValuesRow>
              <SliderValueDisplay variant="body2">
                {props.filterParameters?.minimum_term} {props.filterParameters?.minimum_term === 1 ? 'year' : 'years'}
              </SliderValueDisplay>
              <SliderValueDisplay variant="body2">
                {props.filterParameters?.maximum_term} {props.filterParameters?.maximum_term === 1 ? 'year' : 'years'}
              </SliderValueDisplay>
            </SliderValuesRow>
          </SliderContainer>
          <InputContainer isMobile={isMobile}>
            <ReadOnlyTextField
              value={readOnlyTermValue}
              variant="outlined"
              size="small"
              color="secondary"
              // @ts-ignore
              readOnly
            />
          </InputContainer>
        </SliderRow>
      </FieldWrapper>
      <FieldWrapper>
        <SectionTitle textVariant="subtitle1">Monthly Repayment</SectionTitle>
        <SliderRow>
          <SliderContainer isMobile={isMobile}>
            <StyledSlider3
              isXs={isXs}
              min={minimumMonthlyRepayment}
              max={maximumMonthlyRepayment}
              value={repaymentRange}
              onChangeCommitted={onRepaymentChangeCommitted}
              onChange={onRepaymentChange}
              step={1}
              disabled={props.isLoading}
            />
            <SliderValuesRow>
              <SliderValueDisplay variant="body2">
                {formatCurrencyWithSymbol(minimumMonthlyRepayment, props.systemDetails?.currency_symbol, locale, 0)}
                /mo
              </SliderValueDisplay>
              <SliderValueDisplay variant="body2">
                {formatCurrencyWithSymbol(maximumMonthlyRepayment, props.systemDetails?.currency_symbol, locale, 0)}
                /mo
              </SliderValueDisplay>
            </SliderValuesRow>
          </SliderContainer>
          <InputContainer isMobile={isMobile}>
            <ReadOnlyTextField
              value={readOnlyRepaymentValue}
              variant="outlined"
              size="small"
              color="secondary"
              // @ts-ignore
              readOnly
            />
          </InputContainer>
        </SliderRow>
      </FieldWrapper>
      {isPro && (
        <CheckboxContainer>
          <FormControlLabel
            label="Include ineligible options"
            control={
              <Checkbox
                checked={!props.pricingParams.show_valid_only}
                onChange={(event, value) => {
                  props.updatePricingParams({ ...props.pricingParams, show_valid_only: !value })
                }}
                disabled={props.isLoading}
              />
            }
          />
          <Typography textVariant="helper1">
            Note: this option is visible only to Pro users, customers will not see any ineligible options. Products that
            are eligible for this system but not with the selected filters will not be shown regardless of this option.
          </Typography>
        </CheckboxContainer>
      )}
    </MainWrapper>
  )
}

export default FinanceMarketplaceFinancingCard
